import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Plus, Save, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLANK = {
  slug: "",
  quote: "",
  name: "",
  title: "",
  org: "",
  theme: "",
  source: "",
  order: 0,
  published: true,
};

const SOURCE_OPTIONS = ["", "LinkedIn", "Private", "Email", "Speaking"];

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/testimonials`, { withCredentials: true });
      setItems(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    try {
      if (payload.id) {
        const { id, ...body } = payload;
        await axios.patch(`${API}/admin/testimonials/${id}`, body, { withCredentials: true });
        toast.success("Updated");
      } else {
        await axios.post(`${API}/admin/testimonials`, payload, { withCredentials: true });
        toast.success("Created");
      }
      setEditing(null);
      setCreating(false);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map(x => x.msg).join(", ") : d || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, { withCredentials: true });
      setItems((prev) => prev.filter((c) => c.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-testimonials">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <p className="eyebrow">Content</p>
          <h1 className="mt-3 font-display text-4xl md:text-5xl leading-tight tracking-tight">Testimonials</h1>
          <p className="mt-3 text-[#A9A9A6] text-sm max-w-lg">Peer quotes shown on Home (top 3) and the About page (all).</p>
        </div>
        <button
          onClick={() => { setCreating(true); setEditing({ ...BLANK, order: items.length + 1 }); }}
          data-testid="test-add"
          className="btn-primary"
        >
          <Plus size={14} /> New testimonial
        </button>
      </div>

      {loading ? (
        <div className="mt-10 text-[#A9A9A6]">Loading…</div>
      ) : (
        <div className="mt-8 border border-[#343437]">
          {items.length === 0 && (
            <div className="p-8 text-[#A9A9A6] text-sm">No testimonials yet.</div>
          )}
          {items.map((c) => (
            <div
              key={c.id}
              className="border-t border-[#343437] first:border-t-0 p-5 lg:p-6 grid grid-cols-1 lg:grid-cols-[60px_1fr_170px_160px] gap-4 items-center"
              data-testid={`test-row-${c.slug}`}
            >
              <span className="oversize text-2xl text-[#D8A936]">{String(c.order).padStart(2, "0")}</span>
              <div className="min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <p className="font-display text-[17px] text-[#FAF9F6]">{c.name}</p>
                  {c.source && (
                    <span
                      data-testid={`test-row-source-${c.slug}`}
                      className="text-[9px] tracking-[0.18em] uppercase border border-[#D8A936]/50 text-[#D8A936] px-1.5 py-0.5"
                    >
                      {c.source}
                    </span>
                  )}
                  {c.theme && (
                    <span className="text-xs text-[#A9A9A6]">· {c.theme}</span>
                  )}
                </div>
                {c.title && (
                  <p
                    data-testid={`test-row-title-${c.slug}`}
                    className="text-[11px] text-[#A9A9A6] mt-0.5 leading-snug"
                  >
                    {c.title}
                  </p>
                )}
                <p className="text-xs text-[#A9A9A6] mt-1 line-clamp-1 italic">"{c.quote}"</p>
              </div>
              <span className={`text-[10px] tracking-[0.22em] uppercase ${c.published ? "text-[#D8A936]" : "text-[#A9A9A6]"}`}>
                {c.published ? c.org : "Unpublished"}
              </span>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setEditing(c)}
                  data-testid={`test-edit-${c.slug}`}
                  className="px-3 py-1.5 border border-[#343437] text-xs tracking-widest uppercase hover:border-[#D8A936] hover:text-[#D8A936] transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => remove(c.id)}
                  data-testid={`test-delete-${c.slug}`}
                  className="p-1.5 border border-[#343437] hover:border-red-400 hover:text-red-400 transition-colors"
                  aria-label="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <EditorModal
          initial={editing}
          isNew={creating}
          onCancel={() => { setEditing(null); setCreating(false); }}
          onSave={save}
        />
      )}
    </div>
  );
}

function EditorModal({ initial, isNew, onCancel, onSave }) {
  const [form, setForm] = useState({ ...initial });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    onSave({ ...form, order: Number(form.order) || 0 });
  };

  return (
    <div className="fixed inset-0 z-[80] bg-[#0B0B0C]/85 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-auto">
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#151517] border border-[#343437] w-full max-w-2xl p-8 space-y-5 relative my-8"
        data-testid="test-editor"
      >
        <button type="button" onClick={onCancel} className="absolute top-4 right-4 text-[#A9A9A6] hover:text-[#FAF9F6]" aria-label="Close">
          <X size={18} />
        </button>
        <p className="eyebrow">{isNew ? "New" : "Edit"}</p>
        <h2 className="font-display text-2xl">Testimonial</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label="Slug" value={form.slug} onChange={(v) => set("slug", v)} required testid="test-slug" />
          <TextField label="Order" type="number" value={form.order} onChange={(v) => set("order", v)} testid="test-order" />
        </div>
        <TextArea label="Quote" rows={5} value={form.quote} onChange={(v) => set("quote", v)} required testid="test-quote" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <TextField label="Name" value={form.name} onChange={(v) => set("name", v)} required testid="test-name" />
          <TextField label="Org" value={form.org} onChange={(v) => set("org", v)} testid="test-org" />
          <TextField label="Theme" value={form.theme} onChange={(v) => set("theme", v)} testid="test-theme" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField
            label="Title / role (shown under name)"
            value={form.title}
            onChange={(v) => set("title", v)}
            testid="test-title"
          />
          <label className="block">
            <span className="eyebrow block mb-2">Source</span>
            <select
              value={form.source ?? ""}
              onChange={(e) => set("source", e.target.value)}
              data-testid="test-source"
              className="w-full bg-transparent border border-[#343437] text-[#FAF9F6] px-3 py-2.5 outline-none focus:border-[#D8A936]"
            >
              {SOURCE_OPTIONS.map((o) => (
                <option key={o || "none"} value={o} className="bg-[#151517]">
                  {o || "— none —"}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="flex items-center gap-3 text-sm text-[#FAF9F6]">
          <input
            type="checkbox"
            checked={form.published}
            onChange={(e) => set("published", e.target.checked)}
            className="accent-[#D8A936]"
            data-testid="test-published"
          />
          Published
        </label>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onCancel} className="btn-ghost">Cancel</button>
          <button type="submit" data-testid="test-save" className="btn-primary">
            <Save size={14} /> {isNew ? "Create" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

function TextField({ label, value, onChange, type = "text", required, testid }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}{required && <span className="text-[#D8A936]"> *</span>}</span>
      <input
        type={type}
        required={required}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-transparent border border-[#343437] text-[#FAF9F6] px-3 py-2.5 outline-none focus:border-[#D8A936]"
      />
    </label>
  );
}

function TextArea({ label, value, onChange, required, testid, rows = 4 }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}{required && <span className="text-[#D8A936]"> *</span>}</span>
      <textarea
        rows={rows}
        required={required}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-transparent border border-[#343437] text-[#FAF9F6] px-3 py-2.5 outline-none focus:border-[#D8A936] resize-none"
      />
    </label>
  );
}
